/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {EfdbName} from '../../constants/efdbNameConstants';
import FilteredUnit from '../../constants/FilteredUnit';
import InformativeText from '../../constants/InformativeText';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import capitalGoodsAPI from '../../services/modules/IP/capitalGoodAPI';
import capitalGoodsJSON from '../../services/modules/JSON/capitalGoods';
import supplierInfo from '../../services/modules/OM/supplierInfoDetails';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockFree} from '../../utils/SubscriptionHelper';
import CapGoodsSupplier from '../DisplayModal/CapitalGoodsDis/CapGoodsSupplierSpec';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import AverageForm from '../Forms/AverageForm';
import {AverageKey, AverageLabel} from '../Forms/Constants/IP/AverageKeyLabel';
import {
  yearKeyLabel,
  facilityKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {upstreamKey} from '../Forms/Constants/IP/UpstreamKeyLabel';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import TableTitleHeader from './Utils/TableTitleHeader';

const AverageTable: React.FC<{
  selectedYear: string;
  facilityId: string;
  isAverage?: boolean;
}> = ({selectedYear, facilityId, isAverage = true}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    capitalGoods: number;
    noOfUnits: number;
    costOfEachUnit: number;
    comment: string;
  }

  const method = isAverage ? 'Average' : 'Supplier specific';
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const {capitalGoods} = useJSONStore.getState();
  const [displayTable, setDisplayTable] = useState(false);
  const [displayTableData, setDisplayTableData] = useState<any[]>([]);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [showModelSupplier, setShowModelSupplier] = useState(false);
  const {selectedCountry} = useContext(SelectedCountryContext);
  const [formFacility, setFormFacility] = useState<string>(facilityId);
  const [formSelectedYear, setFormSelectedYear] =
    useState<string>(selectedYear);

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        capitalGoodsAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${facilityId}/${selectedYear}/${method}`
        ),
        supplierInfo(
          APITypeEnum.GET_ALL,
          undefined,
          `${facilityId}/${selectedYear}/upstream`
        )
      ])
        .then(([data, supplierInfo]) => {
          setDataSource(data ?? []);
          setSuppliers(
            supplierInfo.map((item: any) => {
              return {
                label: item?.[upstreamKey.SUPPLIER_NAME],
                value: item._id,
                capitalGoodsSupplied: item?.capitalGoodsSupplied,
                facility: item?.facility
              };
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  useEffect(() => {
    if (formFacility && formSelectedYear) {
      Promise.all([
        supplierInfo(
          APITypeEnum.GET_ALL,
          undefined,
          `${formFacility}/${formSelectedYear}/upstream`
        )
      ])
        .then(([supplierInfo]) => {
          setSuppliers(
            supplierInfo.map((item: any) => {
              return {
                label: item?.[upstreamKey.SUPPLIER_NAME],
                value: item._id,
                capitalGoodsSupplied: item?.capitalGoodsSupplied,
                facility: item?.facility
              };
            })
          );
        })
        .catch(() => {})
        .finally(() => {});
    }
  }, [formFacility, formSelectedYear]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([capitalGoodsJSON()])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      capitalGoodsAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}/${method}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    capitalGoodsAPI(APITypeEnum.DELETE_ALL, {
      capitalgoods: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm
        // clearFilters
      }) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
            {/* <Button
              onClick={() => {
                clearFilters && clearFilters();
                confirm();
              }}
              size="small"
              style={{width: 90}}
            >
              Reset
            </Button> */}
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const averageColumns: Array<defaultCol & ColumnsType<DataType>[0]> =
    useMemo(() => {
      return [
        {
          title: AverageLabel.CAPITAL_GOODS,
          dataIndex: AverageKey.CAPITAL_GOODS,
          editable: true,
          type: 'select',
          ...SearchFilter(
            AverageLabel.CAPITAL_GOODS,
            AverageKey.CAPITAL_GOODS,
            capitalGoods
          ),
          render: (value: any) => {
            return (
              <>
                {
                  capitalGoods.find((item) => {
                    return (
                      item.value === value && item?.dbName === EfdbName.eeio
                    );
                  })?.label
                }
              </>
            );
          }
        },
        {
          title: AverageLabel.NO_UNITS,
          type: 'number',
          dataIndex: AverageKey.NO_UNITS,
          editable: true,
          render: (value) => {
            return formatIndian(value);
          }
          // ...sorter(AverageKey.NO_UNITS as keyof DataType)
        },
        {
          title: AverageLabel.COST_EACH_UNITS,
          type: 'number',
          dataIndex: AverageKey.COST_EACH_UNITS,
          editable: true,
          render: (value) => {
            return value
              ? `${FilteredUnit(selectedCountry).currency} ${formatIndian(
                  value
                )}`
              : '-';
          }
          // ...sorter(AverageKey.COST_EACH_UNITS as keyof DataType)
        },
        {
          title: AverageLabel.POWER_CAPACITY,
          type: 'number',
          dataIndex: AverageKey.POWER_CAPACITY,
          render: (value) => {
            return value
              ? `${formatIndian(value)} ${
                  FilteredUnit(selectedCountry).powerCapacity
                }`
              : '-';
          }
          // ...sorter(AverageKey.COST_EACH_UNITS as keyof DataType)
        },
        ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          ? [
              {
                title: makeCheckKeyLabel.STATUS_COMMENTS.label,
                dataIndex: 'status',
                editable: true,
                type: 'select',
                render: (value: any, record: any) => {
                  const colorText = getStatusColor(value);

                  return (
                    <div
                      style={{
                        fontWeight: 700,
                        color: colorText.color,
                        cursor: 'pointer'
                      }}
                    >
                      {colorText.text}{' '}
                      {value === 'rejected' && record?.comment && (
                        <span className="customComment">
                          {' '}
                          - {record.comment}
                        </span>
                      )}
                    </div>
                  );
                }
              }
            ]
          : []),
        ...(hasUserRole(UserRole.Checker_Role) ||
        hasUserRole(UserRole.Both_role)
          ? [
              {
                title: makeCheckKeyLabel.STATUS.label,
                dataIndex: 'status',
                editable: true,
                type: 'select',
                render: (value: any, record: any) => {
                  const colorText = getStatusColor(value);

                  return (
                    <Tag
                      onClick={() => {
                        if (shouldBlockFree()) {
                          usePlatformStore
                            .getState()
                            .setOpenOnFreeTrialMessageModel(true);

                          return;
                        }

                        setShowModelSupplier(true);
                        setEditData(record);
                        setIsEdit(true);
                      }}
                      color={colorText.color}
                      style={{
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {colorText.text}
                    </Tag>
                  );
                }
              }
            ]
          : []),
        ...(hasUserRole(UserRole.Checker_Role) ||
        hasUserRole(UserRole.Both_role)
          ? [
              {
                title: FullInfo.title,
                dataIndex: 'FullInfo',
                editable: true,
                type: 'select',
                render: (_: any, record: any) => {
                  return (
                    <Button
                      onClick={() => {
                        if (shouldBlockFree()) {
                          usePlatformStore
                            .getState()
                            .setOpenOnFreeTrialMessageModel(true);

                          return;
                        }

                        setShowModelInfo(true);
                        setEditData(record);
                        setIsEdit(true);
                      }}
                      shape="circle"
                      style={{
                        marginRight: 16,
                        cursor: 'pointer',
                        color: '#435B37',
                        background: '#F5FBF3',
                        border: 0
                      }}
                    >
                      <ExclamationOutlined />
                    </Button>
                  );
                }
              }
            ]
          : []),
        ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          ? [
              {
                title: Title.actionTitle,
                dataIndex: 'operation',
                type: '' as const,
                fixed: 'right' as const,
                width: '200px',
                render: (value: any, record: any) =>
                  dataSource.length >= 1 ? (
                    <ActionsButton
                      record={record}
                      setShowModel={setShowModel}
                      setEditData={setEditData}
                      setIsEdit={setIsEdit}
                      handleDelete={handleDelete}
                    />
                  ) : null
              }
            ]
          : [])
      ];
    }, [dataSource]);

  const capitalGoodsKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: AverageKey.CAPITAL_GOODS,
      label: AverageLabel.CAPITAL_GOODS,
      type: objTypes.json,
      format: capitalGoods
    },
    {
      key: AverageKey.NO_UNITS,
      label: AverageLabel.NO_UNITS,
      type: objTypes.number
    },
    {
      key: AverageKey.COST_EACH_UNITS,
      label: AverageLabel.COST_EACH_UNITS,
      type: objTypes.number,
      unit: FilteredUnit(selectedCountry).currency,
      isCost: true
    },
    {
      key: AverageKey.POWER_CAPACITY,
      label: AverageLabel.POWER_CAPACITY,
      type: objTypes.number,
      unit: FilteredUnit(selectedCountry).powerCapacity
    },
    {
      key: AverageKey.SUPPLIER_DATA,
      label: AverageLabel.SUPPLIER_DATA,
      type: objTypes.supplierData,
      format: suppliers,
      isGreen: true,
      renderType: 'multi',
      hidden: isAverage
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: AverageLabel.CAPITAL_GOODS,
        dataIndex: AverageKey.CAPITAL_GOODS,
        editable: true,
        type: 'select',
        ...SearchFilter(
          AverageLabel.CAPITAL_GOODS,
          AverageKey.CAPITAL_GOODS,
          capitalGoods
        ),
        render: (value) => {
          return (
            <>
              {
                capitalGoods.find((item) => {
                  return item.value === value && item?.dbName === EfdbName.eeio;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: AverageLabel.NO_UNITS,
        type: 'number',
        dataIndex: AverageKey.NO_UNITS,
        editable: true,
        // ...sorter(AverageKey.NO_UNITS as keyof DataType)
        render: (value) => {
          return formatIndian(value);
        }
      },
      {
        title: AverageLabel.COST_EACH_UNITS,
        type: 'number',
        dataIndex: AverageKey.COST_EACH_UNITS,
        editable: true,
        render: (value) => {
          return value
            ? `${FilteredUnit(selectedCountry).currency} ${formatIndian(value)}`
            : '-';
        }
        // ...sorter(AverageKey.COST_EACH_UNITS as keyof DataType)
      },

      {
        title: AverageLabel.SUPPLIER_DATA,
        dataIndex: AverageKey.SUPPLIER_DATA,
        editable: true,
        render: (value) => {
          return (
            <div
              onClick={() => {
                if (shouldBlockFree()) {
                  usePlatformStore
                    .getState()
                    .setOpenOnFreeTrialMessageModel(true);

                  return;
                }

                setDisplayTable(true);
                setDisplayTableData(value);
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{value?.length}</div>
            </div>
          );
        }
      },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: AverageLabel.STATUS_COMMENTS,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: AverageLabel.STATUS,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockFree()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }

                      setShowModelSupplier(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      if (shouldBlockFree()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }

                      setShowModelSupplier(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  const hideUserModal = () => {
    setShowModel(false);
  };

  return (
    <>
      <div>
        <TableTitleHeader
          title="Capital Goods Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Capital Good'
          }}
          sampleDownload={{href: SampleExcel.CapitalGoodsAverage.default}}
          jobs={{setShowJobs, uploadType: uploadTypes.CapitalGoodsAverage}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          showAddOnly={!isAverage}
          tooltip={
            isAverage
              ? InformativeText.IP.capitalGoods.main
              : InformativeText.IP.capitalGoods.supSpecific
          }
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />

        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          type={uploadTypes.CapitalGoodsAverage}
          details={`${facilityId}/${selectedYear}`}
        />
        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item) => {
                        return item._id;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={isAverage ? (averageColumns as any) : (columns as any)}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />
        <DrawerForm
          open={showModel}
          onCancel={hideUserModal}
          data={isEdit ? editData : undefined}
          setRefresh={setRefresh}
          FormComponent={
            <AverageForm
              setSubmitDisabled={setSubmitDisabled}
              suppliers={suppliers}
              isAverage={isAverage}
              data={isEdit ? editData : undefined}
              setFormFacility={setFormFacility}
              setFormSelectedYear={setFormSelectedYear}
            />
          }
          API={capitalGoodsAPI}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          type={{key: 'method', value: method}}
          buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
          stepsTitle={['Configure Details', 'Capital Goods Details']}
          formPreview={capitalGoodsKeyLabel}
        />
        <DisplayModal
          title={AverageLabel.SUPPLIER_DATA}
          open={displayTable}
          onCancel={() => {
            setDisplayTable(false);
          }}
          displayComponent={
            <CapGoodsSupplier
              dataSource={displayTableData}
              suppliers={suppliers}
            />
          }
          isTable={true}
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={capitalGoodsKeyLabel} />
        }
        API={capitalGoodsAPI}
        stepsTitle={['All Data']}
        formPreview={capitalGoodsKeyLabel}
        isAction={false}
        isChecker={true}
      />
      {showModelSupplier && (
        <DrawerForm
          open={showModelSupplier}
          onCancel={() => {
            setShowModelSupplier(false);
          }}
          setRefresh={setRefresh}
          title="All Data"
          data={isEdit ? editData : undefined}
          FormComponent={
            <AllDataInfo
              formData={editData}
              formPreview={capitalGoodsKeyLabel}
            />
          }
          API={capitalGoodsAPI}
          isChecker={true}
          stepsTitle={['Status & Comments']}
          formPreview={capitalGoodsKeyLabel}
        />
      )}
    </>
  );
};

export default AverageTable;
